<script>
  import { getUserPermissions } from '../../../helper/permissionsHelper';
  import AppointmentList from './components/appointmentList.vue';
  import AppointmentDetails from './components/appointmentDetails.vue';
  import CreateAppointmentModal from './components/createAppointmentModal.vue';
  import { useAlert } from 'dashboard/composables';
  import { mapGetters, mapActions, mapState } from 'vuex';
  import LoadingState from 'dashboard/components/widgets/LoadingState.vue';

  export default{
    components:{
      AppointmentList,
      AppointmentDetails,
      CreateAppointmentModal,
      LoadingState,
    },
    computed: {
      ...mapState('notifications', ['meta']),
      ...mapState('appointments', {
        storeAppointmentList: 'appointmentList',
        storeFilters: 'filters',
        storePagination: 'pagination',
        storeSorting: 'sorting',
        statusCount: 'statusCount',
      }),
      ...mapGetters('appointments', ['getTrackingStatus']),
      ...mapGetters({
        accountId: 'getCurrentAccountId',
        currentUser: 'getCurrentUser',
      }),
      sortedList() {
        // Use store-managed appointments which are already sorted by the API
        return this.storeAppointmentList.results || [];
      },
      currentPage() {
        return this.storePagination.page;
      },
      totalPages() {
        return this.storePagination.total_pages;
      },
      hasNextPage() {
        return this.storeAppointmentList.next !== -1;
      },
      hasPreviousPage() {
        return this.storePagination.page > 1;
      },
      pageSize() {
        return this.storePagination.page_size || 10;
      },
      totalCount() {
        return this.storePagination.total_count || 0;
      },
      // For results summary - calculate the range of results being displayed
      resultsRange() {
        const start = (this.currentPage - 1) * this.pageSize + 1;
        const end = Math.min(this.currentPage * this.pageSize, this.totalCount);
        return { start, end };
      }
    },
    watch: {
      appointmentFilter() {
        // When status changes, reset service and branch filters
        this.serviceFilter = "";
        this.branchFilter = "";
        // Apply status filter and update cached options
        this.applyStatusFilter();
      },
      serviceFilter() {
        // Just apply the filter, don't update cache
        this.applyFilters(false);
      },
      branchFilter() {
        // Just apply the filter, don't update cache
        this.applyFilters(false);
      },
      typeFilter() {
        // Just apply the filter, don't update cache
        this.applyFilters(false);
      },
      storeFilters(newVal){
        this.appointmentFilter = newVal.status;
      },
      // Watch sortedList to update selected appointment when list changes
      sortedList: {
        handler(newList) {
          if (this.selected) {
            // Find the updated appointment data in the new list
            const updatedAppointment = newList.find(
              appointment => appointment.appointment_id === this.selected.appointment_id
            );
            // Update selected with new data if found
            if (updatedAppointment) {
              this.selected = updatedAppointment;
            }
          }
        },
        deep: true
      }
    },
    data(){
      return{
        appointmentFilter: "",
        serviceFilter: "",
        branchFilter: "",
        typeFilter: "",
        selected: null,
        defaultReminderValue: "",
        filters: [
          { label: this.$t('APPOINTMENTS.INDEX.STATUS_FILTERS.UPCOMING'), value: "upcoming" },
          { label: this.$t('APPOINTMENTS.INDEX.STATUS_FILTERS.REQUESTED'), value: "requested" },
          { label: this.$t('APPOINTMENTS.INDEX.STATUS_FILTERS.CONFIRMED'), value: "confirmed" },
          { label: this.$t('APPOINTMENTS.INDEX.STATUS_FILTERS.RESCHEDULED'), value: "rescheduled" },
          { label: this.$t('APPOINTMENTS.INDEX.STATUS_FILTERS.ARCHIVE'), value: "archive" },
        ],
        reminderOptions: [
          { value: 30, label: this.$t('APPOINTMENTS.INDEX.REMINDER_OPTIONS.THIRTY_MIN') },
          { value: 60, label: this.$t('APPOINTMENTS.INDEX.REMINDER_OPTIONS.ONE_HOUR') },
          { value: 120, label: this.$t('APPOINTMENTS.INDEX.REMINDER_OPTIONS.TWO_HOURS') },
          { value: 1440, label: this.$t('APPOINTMENTS.INDEX.REMINDER_OPTIONS.ONE_DAY') }
        ],
        allAppointments: [], // Complete list of appointments (unfiltered)
        filteredAppointments: [], // Appointments after filtering
        serviceMap: {}, // Map of service name to an array of service IDs
        branchMap: {}, // Map of branch name to an array of branch IDs
        allServices: [], // Complete list of unique service names
        allBranches: [], // Complete list of branch names
        
        // Cache for filter options by status
        cachedFilterOptions: {
          // Will store options in the format:
          // 'upcoming': { serviceMap: {...}, branchMap: {...}, allServices: [...], allBranches: [...] }
          // 'confirmed': { serviceMap: {...}, branchMap: {...}, allServices: [...], allBranches: [...] }
          // etc.
        },
        
        isCreatingAppointment: false,
        isCreatingBranch: false,
        isAscending: true,
        isLoading: false,
        allRequests: [
          { id: 1, name: this.$t('APPOINTMENTS.INDEX.REQUEST_TYPES.BOOKING') },
          { id: 2, name: this.$t('APPOINTMENTS.INDEX.REQUEST_TYPES.TALK_TO_AGENT') },
          { id: 3, name: this.$t('APPOINTMENTS.INDEX.REQUEST_TYPES.RESCHEDULE') },
          { id: 4, name: this.$t('APPOINTMENTS.INDEX.REQUEST_TYPES.CANCELLATION') },
        ],
        trackingStatus: null,
        showSettings: false,
      }
    },
    methods: {
      ...mapActions('appointments', [
        'fetchAppointments',
        'fetchBranch',
        'updateFilters',
        'fetchFilteredAppointments',
        'updatePagination',
        'updateSorting',
        'fetchTrackingStatus',
        'updateTrackingStatus',
        'resetFiltersAndFetch'
      ]),
      ...mapActions('notifications', ['addNotification']),
      handleClick(appointment) {
        this.selected = appointment;
      },
      toggleSort() {
        this.isAscending = !this.isAscending;
        // Update sorting in store and fetch results
        this.updateSorting({
          sort_order: this.isAscending ? 'asc' : 'desc'
        });
      },
      handleCreate(){
        this.isCreatingAppointment = true;
      },
      handleUpdate(){
        // Save tracking status
        this.$store.dispatch('appointments/updateTrackingStatus', this.trackingStatus);
        
        // Save default reminder value to localStorage
        if (this.defaultReminderValue) {
            localStorage.setItem('defaultReminderValue', this.defaultReminderValue.toString());
        }
        
        useAlert(this.$t('APPOINTMENTS.INDEX.ALERTS.SETTINGS_UPDATED'));
      },
      /**
       * Apply status filter and update cached options
       * This is called when appointmentFilter changes
       */
      async applyStatusFilter() {
        try {
          this.isLoading = true;
          
          // Build filter object for API filtering
          const filters = {};
          
          // Handle status filtering
          if (this.appointmentFilter) {
            filters.status = this.appointmentFilter;
          }
          
          // Update store filters and fetch results
          await this.updateFilters(filters);
          
          // Update local list from store
          this.filteredAppointments = this.storeAppointmentList;
          this.allAppointments = this.storeAppointmentList;
          
          // Extract and cache filter options for this status
          this.extractAndCacheFilterOptions();
        } catch (err) {
          useAlert(this.$t('APPOINTMENTS.INDEX.ALERTS.ERROR_FILTER'));
        } finally {
          this.isLoading = false;
        }
      },
      
      /**
       * Apply branch/service filters without updating cached options
       * @param {boolean} updateCache - Whether to update the cached options (default: false)
       */
      async applyFilters(updateCache = false) {
        try {
          this.isLoading = true;
          
          // Build filter object for API filtering
          const filters = {};
          
          // Handle status filtering
          if (this.appointmentFilter) {
            filters.status = this.appointmentFilter;
          }
          
          // Handle service filtering - use cached maps
          if (this.serviceFilter && this.serviceMap[this.serviceFilter]) {
            // Take the first service ID associated with this name for simplicity
            filters.service_id = this.serviceMap[this.serviceFilter][0];
          }
          
          // Handle branch filtering - use cached maps
          if (this.branchFilter && this.branchMap[this.branchFilter]) {
            // Take the first branch ID associated with this name for simplicity
            filters.branch_id = this.branchMap[this.branchFilter][0];
          }
          
          // Update store filters and fetch results
          await this.updateFilters(filters);
          
          // Update local list from store (but not the filters)
          this.filteredAppointments = this.storeAppointmentList;
          this.allAppointments = this.storeAppointmentList;
          
          // Only update cache if explicitly requested
          if (updateCache) {
            this.extractAndCacheFilterOptions();
          }
        } catch (err) {
          useAlert(this.$t('APPOINTMENTS.INDEX.ALERTS.ERROR_FILTER'));
        } finally {
          this.isLoading = false;
        }
      },
      async resetFilters() {
        // Clear local filter state
        this.serviceFilter = "";
        this.branchFilter = "";
        
        // Reset to tracking status instead of clearing all filters
        const defaultStatus = this.trackingStatus || 'upcoming';
        
        // Use the new action that properly resets filters, pagination, and fetches
        await this.resetFiltersAndFetch(defaultStatus);
        
        // Update local lists
        this.filteredAppointments = this.storeAppointmentList;
        this.allAppointments = this.storeAppointmentList;
        
        // Extract and cache filter options for the default status
        this.extractAndCacheFilterOptions();
      },
      resetCreateAppointment(){
        this.isCreatingAppointment = false;
      },
      async handleNextPage() {
        if (!this.hasNextPage) return;
        try {
          this.isLoading = true;
          await this.updatePagination({ page: this.currentPage + 1 });
          // Update local references after the store is updated
          this.allAppointments = this.storeAppointmentList;
          this.filteredAppointments = this.storeAppointmentList;
          
          // Don't update the filter options, use the cached ones
          // We don't want to change the filter options when paginating
        } catch (err) {
          useAlert(this.$t('APPOINTMENTS.INDEX.ALERTS.ERROR_GENERAL'));
        } finally {
          this.isLoading = false;
        }
      },
      
      async handlePreviousPage() {
        if (!this.hasPreviousPage) return;
        try {
          this.isLoading = true;
          await this.updatePagination({ page: this.currentPage - 1 });
          // Update local references after the store is updated
          this.allAppointments = this.storeAppointmentList;
          this.filteredAppointments = this.storeAppointmentList;
          
          // Don't update the filter options, use the cached ones
          // We don't want to change the filter options when paginating
        } catch (err) {
          useAlert(this.$t('APPOINTMENTS.INDEX.ALERTS.ERROR_GENERAL'));
        } finally {
          this.isLoading = false;
        }
      },
      
      async handlePageSizeChange(size) {
        if (size !== this.pageSize) {
          try {
            this.isLoading = true;
            await this.updatePagination({ 
              page: 1, // Reset to first page when changing page size
              page_size: size 
            });
            // Update local references after the store is updated
            this.allAppointments = this.storeAppointmentList;
            this.filteredAppointments = this.storeAppointmentList;
            
            // Don't update the filter options, use the cached ones
            // We don't want to change the filter options when changing page size
          } catch (err) {
            console.error('Error changing page size:', err);
            useAlert(this.$t('APPOINTMENTS.INDEX.ALERTS.ERROR_GENERAL'));
          } finally {
            this.isLoading = false;
          }
        }
      },
      async loadInitialData() {
        try {
          // Set initial filter
          await this.fetchTrackingStatus();
          this.trackingStatus = this.getTrackingStatus;
          this.appointmentFilter = this.trackingStatus;
          const defaultReminderValue = localStorage.getItem('defaultReminderValue');
          if (defaultReminderValue) {
            const value = parseInt(defaultReminderValue);
            const option = this.reminderOptions.find(opt => opt.value === value);
            if (option) {
              this.defaultReminderValue = option.value; // Just store the value, not an array of objects
            }
          } else {
            // Set default to first option's value
            this.defaultReminderValue = this.reminderOptions[0].value;
          }
          
          // Reset other filters
          this.serviceFilter = "";
          this.branchFilter = "";
          
          // Check if we have cached filter options for this status
          const hasCachedOptions = this.loadCachedFilterOptions(this.appointmentFilter);
          
          // Set the status filter in the store and fetch filtered results
          await this.updateFilters({ status: this.trackingStatus });
          
          // Update local lists from store
          this.allAppointments = this.storeAppointmentList;
          this.filteredAppointments = this.storeAppointmentList;
          
          // Only extract and cache if we don't have cached options
          if (!hasCachedOptions) {
            this.extractAndCacheFilterOptions();
          }
          
          this.selected = null;
        } catch (err) {
          console.error('Error loading initial data:', err);
          useAlert(err);
        }
      },
      async handleRefreshDetails(){
        try{
          await this.loadInitialData();
        }catch(err){
          console.error(err);
          useAlert(err);
        }
      },
      /**
       * Extract and cache filter options from current appointments
       * This method caches the filter options by status
       */
      extractAndCacheFilterOptions() {
        // Create temporary maps for services and branches
        const tempServiceMap = {};
        const tempBranchMap = {};
        
        // Get the appointments array from the paginated structure
        const appointments = this.allAppointments.results || [];
        
        // Process each appointment
        appointments.forEach(appointment => {
          // Extract services by name
          if (appointment.service && appointment.service.service_name && appointment.service.service_id) {
            const serviceName = appointment.service.service_name.trim();
            const serviceId = appointment.service.service_id;
            
            // Initialize the array if this is the first time seeing this service name
            if (!tempServiceMap[serviceName]) {
              tempServiceMap[serviceName] = [];
            }
            
            // Add this service ID to the array if it's not already included
            if (!tempServiceMap[serviceName].includes(serviceId)) {
              tempServiceMap[serviceName].push(serviceId);
            }
          }

          // Extract branches by name
          if (appointment.branch && appointment.branch.branch_name && appointment.branch.branch_id) {
            const branchName = appointment.branch.branch_name.trim();
            const branchId = appointment.branch.branch_id;
            
            // Initialize the array if this is the first time seeing this branch name
            if (!tempBranchMap[branchName]) {
              tempBranchMap[branchName] = [];
            }
            
            // Add this branch ID to the array if it's not already included
            if (!tempBranchMap[branchName].includes(branchId)) {
              tempBranchMap[branchName].push(branchId);
            }
          }
        });
        
        // Create filtered lists
        const tempServices = Object.keys(tempServiceMap).map(name => ({
          name: name,
          id: name // Using name as ID for simplicity in the template
        }));
        
        const tempBranches = Object.keys(tempBranchMap).map(name => ({
          name: name,
          id: name // Using name as ID for simplicity in the template
        }));
        
        // Cache the filter options for the current status
        if (this.appointmentFilter) {
          this.cachedFilterOptions[this.appointmentFilter] = {
            serviceMap: { ...tempServiceMap },
            branchMap: { ...tempBranchMap },
            allServices: [...tempServices],
            allBranches: [...tempBranches]
          };
        }
        
        // Update the current filter maps and lists
        this.serviceMap = tempServiceMap;
        this.branchMap = tempBranchMap;
        this.allServices = tempServices;
        this.allBranches = tempBranches;
      },
      
      /**
       * Load cached filter options if available, otherwise extract from current data
       * @param {string} status - The status to load options for
       */
      loadCachedFilterOptions(status) {
        // Check if we have cached options for this status
        if (status && this.cachedFilterOptions[status]) {
          const cached = this.cachedFilterOptions[status];
          
          // Restore from cache
          this.serviceMap = { ...cached.serviceMap };
          this.branchMap = { ...cached.branchMap };
          this.allServices = [...cached.allServices];
          this.allBranches = [...cached.allBranches];
          
          return true;
        }
        
        return false;
      },
      
      /**
       * Legacy method - kept for backward compatibility
       * Now calls extractAndCacheFilterOptions
       */
      extractAllServicesAndBranches() {
        this.extractAndCacheFilterOptions();
      },
      displayFilterDescription(filter) {
        switch(filter) {
          case "upcoming":
            return this.$t('APPOINTMENTS.INDEX.FILTER_DESCRIPTION.FILTER_UPCOMING');
            
          case "archive":
            return this.$t('APPOINTMENTS.INDEX.FILTER_DESCRIPTION.FILTER_ARCHIVE');

          case "confirmed":
            return this.$t('APPOINTMENTS.INDEX.FILTER_DESCRIPTION.FILTER_CONFIRMED');
            
          case "requested":
            return this.$t('APPOINTMENTS.INDEX.FILTER_DESCRIPTION.FILTER_REQUESTED');

          case "rescheduled":
            return this.$t('APPOINTMENTS.INDEX.FILTER_DESCRIPTION.FILTER_RESCHEDULED');
        }
      },
      
      // Helper to combine date and time strings into a Date object
      combineDateTime(dateStr, timeStr) {
        try {
          const datePart = new Date(dateStr);
          if (isNaN(datePart.getTime())) return new Date(); // Invalid date fallback
          
          // Parse time (assuming format like "14:30" or "14:30:00")
          const timeParts = (timeStr || "00:00").split(':');
          const hours = parseInt(timeParts[0], 10) || 0;
          const minutes = parseInt(timeParts[1], 10) || 0;
          
          // Create combined date and time
          const combined = new Date(datePart);
          combined.setHours(hours, minutes, 0, 0);
          return combined;
        } catch (e) {
          console.error('Error parsing date/time:', e);
          return new Date(); // Default to current time on error
        }
      },
    },
    async mounted(){
      try{
        this.isLoading = true;
        await this.loadInitialData();        
        
        // Check if user is service agent
        const userPermissions = getUserPermissions(this.currentUser, this.accountId);
        if (userPermissions.includes('service_agent')) {
          console.log('Current user is a service agent');
          console.log('Service agent email:', this.currentUser.email);
        }
      }catch(err){
        console.error(err);
        useAlert(err);
      }finally{
        this.isLoading = false;
      }
    },
  }
</script>

<template>
  <div class="flex-grow h-full overflow-auto">
    <div class="relative h-full flex flex-col">
      <!-- Modals -->
      <div
        v-if="isCreatingAppointment"
        data-testid="appointments_create_modal_overlay"
        class="fixed inset-0 flex items-center justify-center bg-slate-900/50 dark:bg-slate-800/50 z-50"
      >
        <CreateAppointmentModal 
          @close="isCreatingAppointment = false" @refresh="handleRefreshDetails"
        />
      </div>

      <div
        v-if="showSettings"
        class="fixed inset-0 flex items-center justify-center bg-slate-900/50 dark:bg-slate-800/50 z-50"
      >
        <div class="p-6 border-2 rounded-lg bg-white dark:bg-slate-800 shadow-lg max-w-md w-full mx-auto relative">
          <div class="absolute top-0 left-0 p-1 flex w-full justify-end">
            <woot-button 
              icon="dismiss" 
              color-scheme="secondary" 
              variant="clear" 
              :aria-label="$t('APPOINTMENTS.INDEX.SETTINGS.CLOSE')"
              @click="showSettings = false"
            />
          </div>
          <label>
            {{ $t('APPOINTMENTS.INDEX.SETTINGS.TRACK_BY') }}
            <select v-model="trackingStatus" @change="handleUpdate">
              <option v-for="filter in filters" :key="filter.value" :value="filter.value">{{ filter.label }}</option>
            </select>
            <p class="text-xs text-slate-500">{{ $t('APPOINTMENTS.INDEX.SETTINGS.TRACK_BY_DESCRIPTION') }}</p>
          </label>
           <!-- <label>
            Default reminder:
            <select v-model="defaultReminderValue" @change="handleUpdate">
              <option v-for="option in reminderOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
            </select>
            <p class="text-xs text-slate-500">The count of the selected status will be displayed on menu for ease of reference</p>
          </label> -->
        </div>
      </div>
      

      <!-- Main Content -->
      <div class="p-4 md:p-8 flex flex-col flex-grow gap-4">
        <!-- Header -->
        <div class="flex justify-between items-center">
          <h1 class="flex items-center text-2xl font-medium text-slate-900 dark:text-slate-100">
            <fluent-icon
              icon="calendar"
              class="hidden ml-1 mr-2 rtl:ml-2 rtl:mr-1 md:block"
            />
            {{ $t('APPOINTMENTS.INDEX.HEADER.TITLE') }}
          </h1>
          <woot-button
            data-testid="appointments_create_button"
            variant="smooth"
            color-scheme="primary"
            icon="plus-sign"
            @click="handleCreate"
          >
            {{ $t('APPOINTMENTS.INDEX.HEADER.CREATE_APPOINTMENT') }}
          </woot-button>
        </div>

        <!-- Description -->
        <p class="text-base text-slate-600 dark:text-slate-300">
          {{ $t('APPOINTMENTS.INDEX.HEADER.SUBTITLE') }}
        </p>

        <LoadingState v-if="isLoading" message="Loading appointments"/>

        <!-- Layout Grid -->
        <div v-else-if="!isLoading" class="grid grid-cols-10 gap-4 min-h-0">
          <!-- Status Filters -->
          <div class="col-span-10 flex flex-wrap gap-4 items-center">
            <label 
              v-for="filter in filters" 
              :key="filter.value"
              data-testid="appointments_status_filter"
              :data-status="filter.value"
              class="border-2 dark:border-slate-600 px-4 py-1 rounded-full cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              :class="{ 
                'bg-slate-100 dark:bg-slate-700 border-woot-500 dark:border-woot-500': appointmentFilter === filter.value,
                'text-slate-700 dark:text-slate-300': appointmentFilter !== filter.value
              }"
            >
              <input 
                v-model="appointmentFilter" 
                type="radio" 
                :value="filter.value" 
                class="hidden"
              />
              {{ filter.label + ` (${statusCount[filter.value]})` }}
            </label>

            <button
              data-testid="appointments_sort_toggle"
              @click="toggleSort"
              class="border-2 text-slate-600 dark:text-slate-300 border-slate-600 dark:border-slate-400 p-2 rounded-md cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center transition-colors"
            >
              <span>{{ isAscending ? '↑ Asc' : '↓ Desc' }}</span>
            </button>

            <woot-button 
              icon="settings" 
              color-scheme="secondary" 
              variant="smooth" 
              @click="showSettings = true"
            />
          </div>

          <!-- Status Description -->
          <p class="col-span-10 text-sm text-slate-700 dark:text-slate-300">{{ displayFilterDescription(appointmentFilter) }}</p>

          <!-- Additional Filters (Service and Branch) -->
          <div class="col-span-10 grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            <div class="flex flex-col">
              <label class="mb-1 text-sm text-slate-700 dark:text-slate-300">{{ $t('APPOINTMENTS.INDEX.FILTERS.BRANCH') }}</label>
              <select
                data-testid="appointments_branch_filter"
                v-model="branchFilter"
                class="border dark:border-slate-600 rounded-md py-2 text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-woot-500 dark:focus:ring-woot-600"
              >
                <option value="">{{ $t('APPOINTMENTS.INDEX.FILTERS.ALL_BRANCHES') }}</option>
                <option v-for="branch in allBranches" :key="branch.id" :value="branch.id">
                  {{ branch.name }}
                </option>
              </select>
            </div>

            <div class="flex flex-col">
              <label class="mb-1 text-sm text-slate-700 dark:text-slate-300">{{ $t('APPOINTMENTS.INDEX.FILTERS.SERVICE') }}</label>
              <select
                data-testid="appointments_service_filter"
                v-model="serviceFilter"
                class="border dark:border-slate-600 rounded-md py-2 text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-woot-500 dark:focus:ring-woot-600"
              >
                <option value="">{{ $t('APPOINTMENTS.INDEX.FILTERS.ALL_SERVICES') }}</option>
                <option v-for="service in allServices" :key="service.id" :value="service.name">
                  {{ service.name }}
                </option>
              </select>
            </div>

            <div class="flex items-end">
              <button
                data-testid="appointments_clear_filters_button"
                @click="resetFilters"
                class="border-2 px-4 py-2 rounded-md text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-woot-900/30 hover:bg-slate-100 dark:hover:bg-woot-800/40 transition-colors"
              >
                <fluent-icon icon="delete" size="14" class="inline-block mr-1" />
                {{ $t('APPOINTMENTS.INDEX.FILTERS.CLEAR_FILTERS_BUTTON') }}
              </button>
            </div>
          </div>

          <!-- Pagination -->
          <div class="col-span-10">
            <div class="flex items-center justify-between mb-4">
              <!-- Page Size Selector -->
              <div class="flex items-center space-x-2">
                <select 
                  id="page-size-select"
                  data-testid="appointments_page_size_select"
                  :value="pageSize"
                  @change="handlePageSizeChange(parseInt($event.target.value))"
                  class="m-2 border rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-woot-500"
                >
                  <option value="5">{{ $t('APPOINTMENTS.INDEX.PAGINATION.PER_PAGE', { count: 5 }) }}</option>
                  <option value="10">{{ $t('APPOINTMENTS.INDEX.PAGINATION.PER_PAGE', { count: 10 }) }}</option>
                  <option value="20">{{ $t('APPOINTMENTS.INDEX.PAGINATION.PER_PAGE', { count: 20 }) }}</option>
                  <option value="50">{{ $t('APPOINTMENTS.INDEX.PAGINATION.PER_PAGE', { count: 50 }) }}</option>
                </select>
              </div>

              <!-- Pagination Controls -->
              <div class="flex items-center space-x-2">
                <woot-button
                  data-testid="appointments_prev_page"
                  variant="smooth"
                  color-scheme="secondary"
                  size="small"
                  :disabled="!hasPreviousPage"
                  :aria-label="$t('APPOINTMENTS.INDEX.PAGINATION.PREVIOUS')"
                  @click="handlePreviousPage"
                >
                  <fluent-icon icon="chevron-left" class="h-4 w-4 mr-1" />
                  {{ $t('APPOINTMENTS.INDEX.PAGINATION.PREVIOUS') }}
                </woot-button>

                <div class="hidden md:flex items-center space-x-1">
                  <button
                    :class="{
                      'w-8 h-8 flex items-center justify-center rounded-md text-sm font-medium transition-colors': true,
                      'bg-woot-500 text-white': true,
                    }"
                    :aria-label="$t('APPOINTMENTS.INDEX.PAGINATION.CURRENT_PAGE', { page: currentPage })"
                    aria-current="page"
                  >
                    {{ currentPage }}
                  </button>
                </div>

                <woot-button
                  data-testid="appointments_next_page"
                  variant="smooth"
                  color-scheme="secondary"
                  size="small"
                  :disabled="!hasNextPage"
                  :aria-label="$t('APPOINTMENTS.INDEX.PAGINATION.NEXT')"
                  @click="handleNextPage"
                >
                  {{ $t('APPOINTMENTS.INDEX.PAGINATION.NEXT') }}
                  <fluent-icon icon="chevron-right" class="h-4 w-4 ml-1" />
                </woot-button>
              </div>
            </div>
            
            <!-- Results Summary -->
            <div v-if="sortedList.length > 0" data-testid="appointments_results_summary" class="text-sm text-slate-600 dark:text-slate-300 text-end">
              {{ $t('APPOINTMENTS.INDEX.PAGINATION.RESULTS', { start: resultsRange.start, end: resultsRange.end, total: totalCount }) }}
            </div>
          </div>

          <!-- Appointment List -->
          <div class="col-span-7 flex flex-col gap-2 h-full overflow-auto min-h-0">
            <AppointmentList 
              v-for="(appointment, index) in sortedList" 
              :key="appointment.appointment_id || index" 
              v-bind="appointment"
              :selected="selected && selected.appointment_id === appointment.appointment_id"
              @click.native="handleClick(appointment)"
            />
            <div v-if="sortedList.length === 0" data-testid="appointments_empty_state" class="flex flex-col items-center justify-center p-8 my-4 bg-white dark:bg-slate-800 rounded-lg shadow border border-slate-100 dark:border-slate-700">
              <fluent-icon icon="search" size="32" class="mb-4 text-slate-400 dark:text-slate-500" />
              <p class="text-slate-600 dark:text-slate-300 text-lg font-medium">{{ $t('APPOINTMENTS.INDEX.NO_RESULTS.TITLE') }}</p>
              <p class="text-slate-500 dark:text-slate-400 text-sm mb-4">{{ $t('APPOINTMENTS.INDEX.NO_RESULTS.SUBTITLE') }}</p>
              <button
                data-testid="appointments_empty_clear_filters_button"
                @click="resetFilters"
                class="px-4 py-2 bg-woot-50 dark:bg-woot-900/30 text-woot-600 dark:text-woot-400 rounded-md hover:bg-woot-100 dark:hover:bg-woot-800/40 transition-colors flex items-center gap-2"
              >
                <fluent-icon icon="filter-dismiss" size="16" />
                {{ $t('APPOINTMENTS.INDEX.NO_RESULTS.CLEAR_FILTERS') }}
              </button>
            </div>
          </div>

          <!-- Details Panel -->
          <div class="col-span-3 h-full flex justify-center">
            <AppointmentDetails 
              v-if="selected"
              v-bind="selected"
              @refreshDetails="handleRefreshDetails"
            />
            <div v-else data-testid="appointments_details_placeholder" class="flex flex-col items-center justify-center h-full w-full p-6 bg-white dark:bg-slate-800 rounded-lg shadow border border-slate-100 dark:border-slate-700">
              <fluent-icon icon="hand-point-left" size="32" class="mb-4 text-slate-400 dark:text-slate-500" />
              <p class="text-slate-600 dark:text-slate-300 text-lg font-medium text-center">{{ $t('APPOINTMENTS.INDEX.DETAILS.SELECT_TITLE') }}</p>
              <p class="text-slate-500 dark:text-slate-400 text-sm text-center mt-2">
                {{ $t('APPOINTMENTS.INDEX.DETAILS.SELECT_SUBTITLE') }}
              </p>
            </div>
          </div>
        </div>  
      </div>
    </div>
  </div>
</template>
